import { auth } from "../firebase";

const API_BASE =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5030/auravit-bb843/us-central1"
    : "https://us-central1-auravit-bb843.cloudfunctions.net";

const sendContactChangeOtpEndpoint = `${API_BASE}/sendContactChangeOtp`;
const verifyContactChangeOtpEndpoint = `${API_BASE}/verifyContactChangeOtp`;

/**
 * Send OTP for contact number change
 * @param {string} newPhoneNumber - New phone number to verify
 * @param {string} currentPhoneNumber - Current phone number for security verification
 * @param {string} verificationMethod - 'sms' or 'email'
 * @returns {Promise<Object>} Response with otpId and message
 */
export const sendContactChangeOtp = async (newPhoneNumber, currentPhoneNumber, verificationMethod = "sms") => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }

    const idToken = await user.getIdToken();
    
    console.log("📱 Sending contact change OTP:", { newPhoneNumber, currentPhoneNumber, verificationMethod });

    const response = await fetch(sendContactChangeOtpEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        newPhoneNumber: newPhoneNumber?.trim() || "",
        currentPhoneNumber: currentPhoneNumber?.trim() || "",
        verificationMethod,
      }),
    });

    console.log("📡 Contact change OTP API response status:", response.status);

    const data = await response.json();
    console.log("📨 Contact change OTP API response data:", data);

    if (!response.ok) {
      console.error("❌ Contact change OTP send failed:", data.error);
      
      // Handle special case for missing profile
      if (data.requiresProfileSetup) {
        throw new Error(data.details || "Please complete your profile setup first.");
      }
      
      // Handle special case for missing contact number
      if (data.requiresContactNumber) {
        throw new Error(data.details || "Please add a contact number to your profile first.");
      }
      
      throw new Error(data.error || "Failed to generate OTP");
    }

    return data;
  } catch (error) {
    console.error("❌ sendContactChangeOtp failed:", error);
    throw error;
  }
};

/**
 * Verify OTP for contact number change
 * @param {string} otpId - OTP ID from sendContactChangeOtp
 * @param {string} otp - 6-digit OTP code
 * @returns {Promise<Object>} Response with success status and new phone number
 */
export const verifyContactChangeOtp = async (otpId, otp) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }

    const idToken = await user.getIdToken();
    
    console.log("🔐 Verifying contact change OTP:", { otpId, otp });

    const response = await fetch(verifyContactChangeOtpEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        otpId: otpId.trim(),
        otp: otp.trim(),
      }),
    });

    console.log("📡 Contact change OTP verify response status:", response.status);

    const data = await response.json();
    console.log("📨 Contact change OTP verify response data:", data);

    if (!response.ok) {
      console.error("❌ Contact change OTP verification failed:", data.error);
      
      // Handle special case for missing profile
      if (data.requiresProfileSetup) {
        throw new Error(data.details || "Please complete your profile setup first.");
      }
      
      throw new Error(data.error || "Failed to verify OTP");
    }

    return data;
  } catch (error) {
    console.error("❌ verifyContactChangeOtp failed:", error);
    throw error;
  }
};

/**
 * Validate phone number format
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} True if valid format
 */
export const validatePhoneNumber = (phoneNumber) => {
  const phoneRegex = /^9\d{9}$/;
  return phoneRegex.test(phoneNumber.trim());
};

/**
 * Format phone number for display
 * @param {string} phoneNumber - Phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
  const cleaned = phoneNumber.trim().replace(/\D/g, '');
  if (cleaned.length === 10 && cleaned.startsWith('9')) {
    return `+63 ${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('0')) {
    return `+63 ${cleaned.slice(1, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  }
  return phoneNumber;
};

/**
 * Format phone number for SMS (use +63 format)
 * @param {string} phoneNumber - Phone number to format for SMS
 * @returns {string} Phone number with +63 prefix for SMS
 */
export const formatPhoneNumberForSms = (phoneNumber) => {
  const cleaned = phoneNumber.trim().replace(/\D/g, '');
  if (cleaned.length === 10 && cleaned.startsWith('9')) {
    return `+63${cleaned}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('0')) {
    return `+63${cleaned.slice(1)}`;
  }
  return phoneNumber;
};
